import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as api from '../api/client';
import {
  getDeviceFingerprint,
  getDeviceCookie,
  setDeviceCookie,
} from './fingerprint';

/**
 * AuthContext
 * --------------------------------------------------------------------
 * Drives the admin auth lifecycle.
 *
 *   status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated'
 *
 * On mount (if a cms_token exists) calls /me to validate it. On failure
 * the token is cleared.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [token, setTokenState] = useState(() => api.getToken());
  const [status, setStatus] = useState('idle');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Initial bootstrap.
  useEffect(() => {
    let cancelled = false;
    const existing = api.getToken();
    if (!existing) {
      setStatus('unauthenticated');
      return undefined;
    }
    setStatus('loading');
    api
      .me()
      .then((resp) => {
        if (cancelled) return;
        setAdmin(resp && resp.admin ? resp.admin : null);
        setTokenState(existing);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        api.setToken(null);
        setTokenState(null);
        setAdmin(null);
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async ({ username, password }) => {
    const fingerprint = getDeviceFingerprint();
    const deviceCookie = getDeviceCookie();
    const resp = await api.login({
      username,
      password,
      fingerprint,
      deviceCookie,
    });
    if (resp && resp.token) {
      api.setToken(resp.token);
      setTokenState(resp.token);
      setAdmin(resp.admin || null);
      setStatus('authenticated');
      return { authenticated: true, raw: resp };
    }
    if (resp && resp.requires_otp) {
      return {
        authenticated: false,
        requiresOtp: true,
        adminId: resp.admin_id,
        otpDevDisplay: resp.otp_dev_display || '',
        raw: resp,
      };
    }
    return { authenticated: false, raw: resp };
  }, []);

  const verifyOtp = useCallback(async ({ adminId, otp, trustDevice }) => {
    const fingerprint = getDeviceFingerprint();
    const resp = await api.verifyOtp({
      adminId,
      otp,
      fingerprint,
      trustDevice,
    });
    if (resp && resp.token) {
      api.setToken(resp.token);
      setTokenState(resp.token);
      setAdmin(resp.admin || null);
      if (resp.device_cookie) setDeviceCookie(resp.device_cookie);
      setStatus('authenticated');
      return { authenticated: true, raw: resp };
    }
    return { authenticated: false, raw: resp };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (_) {
      /* ignore — clear locally regardless */
    }
    api.setToken(null);
    setTokenState(null);
    setAdmin(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo(
    () => ({
      admin,
      token,
      status,
      login,
      verifyOtp,
      logout,
    }),
    [admin, token, status, login, verifyOtp, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}

export default AuthContext;
